import { Tabs, Badge } from 'antd';
import type { Tab } from 'rc-tabs/lib/interface';
import { useStoreState } from '../../store';
import { useParams, useNavigate } from 'react-router-dom-v5-compat';
import type { SimulationDetail } from '../../store/simulation/types';
import { resolveSimulationServiceVersion } from '../../store/simulation/resolveSimulationServiceVersion';
import { useSession } from '../../hooks/useSession';
import { SimulationDetailTab } from './SimulationDetailTab';
import { SimulationShipmentsTab } from './SimulationShipmentsTab';
import { SimulationShipmentsTabV2 } from './SimulationShipmentsTabV2';
import { SimulationMappingsTab } from './SimulationMappingsTab';
import { SimulationExportTab } from './SimulationExportTab';
import { SimulationCarrierSelectionReportTab } from './SimulationCarrierSelectionReportTab';
import { SimulationTNTReportTab } from './SimulationTNTReportTab';
import { SimulationOtdReportTab } from './SimulationOtdReportTab';
import { SimulationChoroplethTab } from './SimulationChoroplethTab';
import { SimulationMargingReportTab } from './SimulationMarginReportTab';
import { ShipmentLimitsReportTab } from './ShipmentLimitsReportTab';

export interface SimulationTabProps {
  simulation: SimulationDetail;
}

export enum ReportFilterParams {
  TNT_TYPE = 'tntType',
  CARRIER = 'carrier',
  DAY_OF_WEEK = 'dayOfWeek',
  TENANT_ID = 'tenantId',
  ORIGIN_SLUG = 'originSlug',
  SELL_RATE_ID = 'sellRateId',
  INCLUDE_CARRIER_SURCHARGES = 'includeCarrierSurcharges',
}

export const SimulationDetailTabs = ({ simulation }: SimulationTabProps) => {
  const params = useParams<{ simulationId: string; tabId: string; subTabId?: string }>();
  const activeTabKey = params.tabId || 'simulation';
  const sessionPartnerSlug = useStoreState((state) => state?.session?.user?.sessionPartnerUrl);
  const isArchived = simulation.simulationStatus === 'archived';
  const isV2 = resolveSimulationServiceVersion(simulation.simulationSetupData) === 'v2';
  const { isShipiumView } = useSession();
  const costAdjustConfigId = simulation.simulationShipmentOptions?.costAdjustConfigId;
  const navigate = useNavigate();

  const handleTabSelection = (activeKey: string) => {
    const basePath = `/${sessionPartnerSlug}/simulation/${params.simulationId}/${activeKey}`;
    navigate(basePath);
  };

  const items: Tab[] = [
    {
      key: 'simulation',
      label: (
        <>
          <b>Simulation</b>
        </>
      ),
      children: <SimulationDetailTab simulation={simulation} />,
    },
    {
      key: 'shipments',
      label: (
        <Badge offset={[10, 7]} dot={false}>
          <b>Shipments</b>
        </Badge>
      ),
      children: isV2 ? (
        <SimulationShipmentsTabV2 simulation={simulation} />
      ) : (
        <SimulationShipmentsTab simulation={simulation} />
      ),
      disabled: isArchived,
    },
    {
      key: 'export',
      label: (
        <Badge offset={[10, 7]} dot={false}>
          <b>Export</b>
        </Badge>
      ),
      children: <SimulationExportTab simulation={simulation} />,
      disabled: isArchived,
    },
    ...(simulation.simulationType === 'sell_rate_simulation'
      ? [
          {
            key: 'sell_rate_margin_report',
            label: (
              <Badge offset={[10, 7]} dot={false}>
                <b>Sell Rate Margin Report</b>
              </Badge>
            ),
            children: <SimulationMargingReportTab simulation={simulation} />,
            disabled: isArchived,
          },
        ]
      : []),
    ...(costAdjustConfigId
      ? [
          {
            key: 'shipment_limits_report',
            label: (
              <Badge offset={[10, 7]} dot={false}>
                <b>Shipment Limits Report</b>
              </Badge>
            ),
            children: <ShipmentLimitsReportTab simulation={simulation} />,
            disabled: isArchived,
          },
        ]
      : []),
    {
      key: 'carrier_selection_report',
      label: (
        <Badge offset={[10, 7]} dot={false}>
          <b>Carrier Selection Report</b>
        </Badge>
      ),
      children: <SimulationCarrierSelectionReportTab simulation={simulation} />,
      disabled: isArchived,
    },
    {
      key: 'tnt_reports',
      label: (
        <Badge offset={[10, 7]} dot={false}>
          <b>Time in Transit Report</b>
        </Badge>
      ),
      children: <SimulationTNTReportTab simulation={simulation} />,
      disabled: isArchived,
    },
    ...(simulation.simulationType === 'advanced' && isV2 && isShipiumView
      ? [
          {
            key: 'otd_report',
            label: (
              <Badge offset={[10, 7]} dot={false}>
                <b>OTD Report</b>
              </Badge>
            ),
            children: <SimulationOtdReportTab simulation={simulation} />,
            disabled: isArchived,
          },
        ]
      : []),
    {
      key: 'choropleth',
      label: (
        <Badge offset={[10, 7]} dot={false}>
          <b>Choropleth Report</b>
        </Badge>
      ),
      children: <SimulationChoroplethTab simulation={simulation} />,
      disabled: isArchived,
    },
    {
      key: 'mappings',
      label: (
        <Badge offset={[10, 7]} dot={false}>
          <b>Mappings</b>
        </Badge>
      ),
      children: <SimulationMappingsTab simulation={simulation} />,
      disabled: isArchived,
    },
  ];

  return (
    // destory inactive prevents some map rendering issues but does force additional reloads
    <Tabs
      destroyInactiveTabPane={true}
      onTabClick={handleTabSelection}
      type={'line'}
      activeKey={activeTabKey}
      items={items}
    />
  );
};
